import { createForOfIteratorHelperLoose as _createForOfIteratorHelperLoose } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useCallback, useContext } from 'react';
import { interpolate } from './interpolate.js';

var noop = function noop(x) {
  return x;
};
var warnTranslation = function warnTranslation(key) {
  console.error("useTranslate: Needs to be wrapped in a <TranslationProvider />.");
  return key;
};
var TranslateContext = createContext(warnTranslation);
var TranslationProvider = function TranslationProvider(_ref) {
  var children = _ref.children,
    translations = _ref.translations;
  var t = useCallback(function (key, args) {
    if (args === void 0) {
      args = {};
    }
    return translate(translations, key, args);
  }, [translations]);
  return /*#__PURE__*/React.createElement(TranslateContext.Provider, {
    value: t
  }, children);
};
var TestProvider = function TestProvider(_ref2) {
  var children = _ref2.children,
    _ref2$translate = _ref2.translate,
    translate = _ref2$translate === void 0 ? noop : _ref2$translate;
  return /*#__PURE__*/React.createElement(TranslateContext.Provider, {
    value: translate
  }, children);
};
var translateFallback = function translateFallback(key, reason) {
  if (process.env.NODE_ENV !== "production") {
    console.error("Translate: \"" + key + "\" key is " + reason);
    return "[" + key + "]";
  }
  return "";
};
var translate = function translate(translations, key, args) {
  if (args === void 0) {
    args = {};
  }
  var path = key.split(".");
  var data = translations;
  for (var _iterator = _createForOfIteratorHelperLoose(path), _step; !(_step = _iterator()).done;) {
    var i = _step.value;
    if (typeof data !== "object" || !(i in data)) {
      return translateFallback(key, "missing");
    }
    data = data[i];
  }
  if (!data || typeof data !== "string") {
    return translateFallback(key, "not a string");
  }
  return interpolate(data, args, key);
};
var escapeHtml = function escapeHtml(unsafe) {
  return unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
};
var useTranslate = function useTranslate() {
  return useContext(TranslateContext);
};

/**
 * HTML-safe version of useTranslate.
 *
 * The basic concept to try and decrease the risk of unsafe HTML is to
 * separate the use of `dangerouslySetInnerHTML` and `__html`, enforcing
 * the use of specific known-to-be-safe functions to obtain HTML-safe
 * strings.
 *
 * SAFETY: Safety is predicated on the translation-strings being from a trusted
 * source. They are allowed to contain HTML, as long as it is not interpolating
 * arguments into `<script>` or `<style>` contents, or between `<` and `>`.
 *
 * @example
 *
 *     const MyComponent = (props) => {
 *       const safeTranslate = useHTMLSafeTranslate();
 *
 *       return (
 *         <section dangerouslySetInnerHTML={
 *           safeTranslate("my.path", props)
 *         } />
 *       );
 *     };
 *
 * @returns {HTMLSafeTranslate}
 */
var useHTMLSafeTranslate = function useHTMLSafeTranslate() {
  var translate = useTranslate();
  var htmlSafeTranslate = useCallback(function (key, unsafeArgs) {
    if (unsafeArgs === void 0) {
      unsafeArgs = {};
    }
    var safeArgs = {};
    for (var k in unsafeArgs) {
      if (Object.prototype.hasOwnProperty.call(unsafeArgs, k)) {
        safeArgs[k] = escapeHtml(unsafeArgs[k]);
      }
    }

    // SAFETY: We have now escaped the arguments so they are safe for
    // interpolation, and the translation data pointed to by key is from a
    // trusted source (static translation-file controlled by the
    // application).
    // This way it is ok to use __html and construct a HTMLSafeString.
    return {
      __html: translate(key, safeArgs)
    };
  }, [translate]);
  return htmlSafeTranslate;
};

export { TestProvider, TranslationProvider, useHTMLSafeTranslate, useTranslate };
//# sourceMappingURL=index.js.map
